import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import i18n from '@/i18n';
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/lazyWithRetry';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /** Override the default `errors.boundary.title` copy. */
  title?: string;
  /** Override the default `errors.boundary.description` copy. */
  message?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Catches render errors in its subtree and renders an elegant, i18n-aware
 * fallback UI. Stack traces only render in dev so client demos never see
 * raw error text.
 *
 * Class component is required because React error boundaries are class-only;
 * we read i18n keys via the imported singleton instead of hooks.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Safety net for any lazy() that escaped lazyWithRetry: a stale-deploy chunk
    // error auto-reloads (once per cooldown) instead of showing the broken fallback.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) return;
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- dev-only diagnostic, stripped in prod build
      console.error('[ErrorBoundary]', error, errorInfo);
    }
    this.setState({ error, errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const t = i18n.t.bind(i18n);
      const title = this.props.title ?? (t('errors.boundary.title') as unknown as string);
      const description = this.props.message ?? (t('errors.boundary.description') as unknown as string);

      return (
        <Card className="m-4 border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">{description}</p>

            {import.meta.env.DEV && this.state.error && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  Detalles técnicos
                </summary>
                <pre className="mt-2 overflow-auto rounded bg-muted p-4 text-xs">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-2">
              <Button onClick={this.handleRetry} variant="outline" size="sm">
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('errors.boundary.retry') as unknown as string}
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="ghost"
                size="sm"
              >
                {t('errors.boundary.reload') as unknown as string}
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps: Omit<ErrorBoundaryProps, 'children'> = {}
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export function PageErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

export function WidgetErrorBoundary({ children, name }: { children: ReactNode; name?: string }) {
  const title = name
    ? `${i18n.t('errors.boundary.title') as unknown as string} (${name})`
    : undefined;
  return <ErrorBoundary title={title}>{children}</ErrorBoundary>;
}

export function AIErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

export default ErrorBoundary;
